/**
 * Engagement start-seed — reusable entry overlay (catalog first consumer).
 * Locks preferred L1 + shortens intake so discovery cannot contradict the seed.
 * Never mutates L1 SoT (ADR-007). Not a second journey engine.
 */

const PATTERN_ID_RE = /^PATTERN_[A-Z0-9_]+$/;

/** @typedef {'catalog'|'related_switch'} PatternLockSource */

/**
 * @typedef {object} EngagementStartSeed
 * @property {PatternLockSource} source
 * @property {string} preferredPatternId
 * @property {boolean} lockPattern
 * @property {string[]} discoveryIds
 * @property {string[]} requirementsIds
 * @property {string} [intakeHintKey]
 */

/** Catalog preset — pack-context discovery only (no product_type / subtype). */
const CATALOG_PATTERN_START_DISCOVERY_IDS = ['pack_audience', 'users', 'stage'];
const CATALOG_PATTERN_START_REQUIREMENTS_IDS = [
    'scale',
    'priority_nfr',
    'capability_scope',
    'constraints',
    'integration_landscape',
];

/**
 * @param {string} patternId
 * @returns {EngagementStartSeed | null}
 */
function catalogStartSeed(patternId) {
    const id = String(patternId || '').trim();
    if (!PATTERN_ID_RE.test(id)) return null;
    return {
        source: 'catalog',
        preferredPatternId: id,
        lockPattern: true,
        discoveryIds: [...CATALOG_PATTERN_START_DISCOVERY_IDS],
        requirementsIds: [...CATALOG_PATTERN_START_REQUIREMENTS_IDS],
        intakeHintKey: 'agentic.start_seed.catalog_hint',
    };
}

/**
 * @param {string} [search]
 * @returns {EngagementStartSeed | null}
 */
function startSeedFromSearch(search) {
    try {
        const q = String(search || '');
        const params = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);
        return catalogStartSeed(params.get('pattern') || '');
    } catch {
        return null;
    }
}

/**
 * Rebuild seed from persisted engagement fields (avoids storing ID lists in Cosmos).
 * @param {{ patternLockSource?: string | null, preferredPatternId?: string | null }} state
 * @returns {EngagementStartSeed | null}
 */
function startSeedFromState(state) {
    if (!state || state.patternLockSource !== 'catalog') return null;
    return catalogStartSeed(state.preferredPatternId || '');
}

/**
 * @param {object | null | undefined} profile - IntentIntakeProfile-like
 * @param {EngagementStartSeed | null | undefined} seed
 * @returns {{ discoveryIds: string[], requirementsIds: string[], seedAnswers?: object, productTypeFilter?: string[], intakeHintKey?: string }}
 */
function applyStartSeedIntake(profile, seed) {
    const base = profile && typeof profile === 'object' ? { ...profile } : {};
    const discoveryIds = Array.isArray(base.discoveryIds) ? [...base.discoveryIds] : [];
    const requirementsIds = Array.isArray(base.requirementsIds) ? [...base.requirementsIds] : [];
    if (!seed || !seed.lockPattern) {
        return {
            ...base,
            discoveryIds,
            requirementsIds,
        };
    }
    return {
        ...base,
        discoveryIds: [...(seed.discoveryIds || [])],
        requirementsIds: [...(seed.requirementsIds || [])],
        intakeHintKey: seed.intakeHintKey || base.intakeHintKey,
    };
}

/**
 * When locked, architecture must use this id; otherwise null (caller runs resolver).
 * @param {{ patternLockSource?: string | null, preferredPatternId?: string | null }} state
 * @returns {string | null}
 */
function resolveLockedPreferredPatternId(state) {
    if (!state?.patternLockSource) return null;
    const id = String(state.preferredPatternId || '').trim();
    return PATTERN_ID_RE.test(id) ? id : null;
}

function isPatternLocked(state) {
    return Boolean(resolveLockedPreferredPatternId(state));
}

module.exports = {
    PATTERN_ID_RE,
    CATALOG_PATTERN_START_DISCOVERY_IDS,
    CATALOG_PATTERN_START_REQUIREMENTS_IDS,
    catalogStartSeed,
    startSeedFromSearch,
    startSeedFromState,
    applyStartSeedIntake,
    resolveLockedPreferredPatternId,
    isPatternLocked,
};
